import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/config/env';
import { tokenStorage } from '@/lib/token-storage';
import { decodeJwtPayload } from '@/lib/jwt';

/** Broadcast a forced logout so the auth layer can react (clear + redirect). */
export const AUTH_LOGOUT_EVENT = 'app:auth-logout';
function forceLogout() {
  tokenStorage.clear();
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
}

/** `exp` (absolute UNIX seconds) from a JWT payload, or null if unparsable. */
function jwtExp(token: string): number | null {
  const exp = Number(decodeJwtPayload(token).exp);
  return Number.isFinite(exp) ? exp : null;
}

const KC_TOKEN_URL = () =>
  `${env.keycloak.url}/realms/${env.keycloak.realm}/protocol/openid-connect/token`;

/* ── Access-token refresh (Keycloak refresh_token grant) ─────────────────── */
let refreshInFlight: Promise<string | null> | null = null;
async function refreshAccessToken(): Promise<string | null> {
  const refresh = tokenStorage.refresh;
  if (!refresh) return null;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = axios
    .post(
      KC_TOKEN_URL(),
      new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: env.keycloak.clientId,
        refresh_token: refresh,
      }),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
    )
    .then((res) => {
      const access = res.data?.access_token;
      if (!access) return null;
      tokenStorage.set({
        access,
        refresh: res.data.refresh_token ?? refresh,
        expSeconds: jwtExp(access) ?? undefined,
      });
      // A new access token invalidates the derived RPT.
      rptCache = null;
      return access as string;
    })
    .catch(() => null)
    .then((access) => {
      if (!access) forceLogout();
      return access;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/**
 * UMA authorization token (RPT). The DataCentral v4 API authorizes resources via
 * Keycloak UMA: the plain access token authenticates but returns EMPTY rows;
 * the API expects a Requesting Party Token obtained by exchanging the access
 * token (grant `uma-ticket`, audience = the resource-server client). The RPT
 * carries the `authorization` claim and is what unlocks data — mirrors the
 * legacy `vnetwork-auth-sdk` (getUtmTicketToken). Held in memory only.
 */
let rptCache: { token: string; expMs: number } | null = null;
let rptInFlight: Promise<string | null> | null = null;

async function exchangeRpt(accessToken: string): Promise<string | null> {
  const res = await axios.post(
    KC_TOKEN_URL(),
    new URLSearchParams({
      grant_type: 'urn:ietf:params:oauth:grant-type:uma-ticket',
      audience: env.keycloak.serverClientId,
    }),
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  );
  const rpt = res.data?.access_token as string | undefined;
  if (!rpt) return null;
  const exp = jwtExp(rpt);
  rptCache = { token: rpt, expMs: exp ? exp * 1000 : Date.now() + 60_000 };
  return rpt;
}

/** Get a valid RPT — refreshing the access token first if it's expired. */
async function getRpt(): Promise<string | null> {
  if (rptCache && Date.now() < rptCache.expMs - 30_000) return rptCache.token;
  if (rptInFlight) return rptInFlight;
  rptInFlight = (async () => {
    let access = tokenStorage.access;
    if (!access) return null;
    if (tokenStorage.isExpired) access = await refreshAccessToken();
    if (!access) return null;
    return exchangeRpt(access);
  })()
    .catch(() => null)
    .finally(() => {
      rptInFlight = null;
    });
  return rptInFlight;
}

/**
 * Groups claim (e.g. "/Tech", "/Admin") from the cached UMA RPT. The RAW access
 * token has NO groups claim — only the RPT does — so employee-role gating in the
 * UI must read this, not the access token. Returns [] until the first authz-gated
 * request has warmed the RPT cache (ops pages fetch via opsApi first, so by the
 * time role-gated buttons render the cache is populated). Best-effort ensure()
 * warms it and dispatches an event so gated UI can re-check.
 */
export function rptGroupsSync(): string[] {
  if (!rptCache) return [];
  try {
    const payload = JSON.parse(atob(rptCache.token.split('.')[1] ?? ''));
    return Array.isArray(payload?.groups) ? payload.groups : [];
  } catch {
    return [];
  }
}

export const RPT_READY_EVENT = 'app:rpt-ready';

/** Warm the RPT cache (if not already) so rptGroupsSync() returns groups. */
export async function ensureRptGroups(): Promise<string[]> {
  if (!rptCache) {
    await getRpt().catch(() => null);
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(RPT_READY_EVENT));
  }
  return rptGroupsSync();
}

/* ── Request interceptors ────────────────────────────────────────────────── */
/** Attach the plain access token (refreshing if expired). For non-DataCentral surfaces. */
async function withAccessToken(config: InternalAxiosRequestConfig) {
  let access = tokenStorage.access;
  if (access && tokenStorage.isExpired) access = await refreshAccessToken();
  if (access) config.headers.set('Authorization', `Bearer ${access}`);
  return config;
}

/** Attach the UMA RPT (DataCentral v4 — data is authorization-gated). */
async function withRpt(config: InternalAxiosRequestConfig) {
  const rpt = await getRpt();
  if (rpt) config.headers.set('Authorization', `Bearer ${rpt}`);
  return config;
}

/**
 * Factory: an authenticated axios instance. `authz: true` sends the UMA RPT
 * (DataCentral v4); otherwise the plain access token (other service surfaces).
 */
export function createClient(baseURL: string, opts: { authz?: boolean } = {}): AxiosInstance {
  const authz = opts.authz ?? false;
  const instance = axios.create({ baseURL, timeout: 30_000 });

  instance.interceptors.request.use(authz ? withRpt : withAccessToken);

  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config as
        | (AxiosRequestConfig & { _retried?: boolean })
        | undefined;

      if (status === 401 && original && !original._retried) {
        original._retried = true;
        let bearer: string | null;
        if (authz) {
          rptCache = null; // force a fresh RPT (refreshes the access token too if needed)
          bearer = await getRpt();
        } else {
          bearer = await refreshAccessToken();
        }
        if (bearer) {
          original.headers = { ...original.headers, Authorization: `Bearer ${bearer}` };
          return instance.request(original);
        }
        forceLogout();
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

/** Factory: an unauthenticated axios instance (no token, no 401→logout). */
export function createPublicClient(baseURL: string): AxiosInstance {
  return axios.create({ baseURL, timeout: 30_000 });
}

/**
 * Ready-to-use API clients. DataCentral v4 is authorization-gated → sends the
 * UMA RPT. The shared service surfaces use the plain access token.
 *
 * `opsApi` targets the same @mgmt host as serviceApiV3 but sends the UMA RPT
 * instead of the raw access token. The `/internal/ops/*` routes on @mgmt are
 * gated by EmployeeRequired which checks for groups:["/Admin"] in the token —
 * that claim is only present in the RPT (not the plain access token).
 */
export const dataCentralApi = createClient(env.dataCentralApiUrl, { authz: true });
export const serviceApiV3 = createClient(env.serviceApiV3Url);
export const opsApi = createClient(env.serviceApiV3Url, { authz: true });
export const warehouseApi = createClient(env.warehouseApiUrl);
export const automationApi = createClient(env.automationApiUrl);
export const healthcheckApi = createClient(env.healthcheckApiUrl);
export const livechatApi = createClient(env.livechat.apiUrl);
